/**
 * Notification Store
 *
 * Manages in-app notifications using Zustand.
 */

import { create } from 'zustand';
import toast from 'react-hot-toast';

const useNotificationStore = create((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isLoading: false,

  // Set notifications
  setNotifications: (notifications, unreadCount) => {
    set({ notifications, unreadCount });
  },

  // Add a new notification
  addNotification: (notification) => {
    set((state) => ({
      notifications: [notification, ...state.notifications],
      unreadCount: state.unreadCount + 1,
    }));

    // Show toast for new notifications
    const typeIcons = {
      success: 'check',
      error: 'alert',
      warning: 'warning',
      info: 'info',
      content: 'file',
      reaction: 'heart',
      admin: 'shield',
    };

    toast(notification.message, {
      icon: typeIcons[notification.type] || 'bell',
    });
  },

  // Mark notification as read
  markAsRead: (notificationId) => {
    set((state) => ({
      notifications: state.notifications.map((n) =>
        n.id === notificationId || n._id === notificationId
          ? { ...n, isRead: true }
          : n
      ),
      unreadCount: Math.max(0, state.unreadCount - 1),
    }));
  },

  // Mark all as read
  markAllAsRead: () => {
    set((state) => ({
      notifications: state.notifications.map((n) => ({ ...n, isRead: true })),
      unreadCount: 0,
    }));
  },

  // Remove notification
  removeNotification: (notificationId) => {
    set((state) => {
      const notification = state.notifications.find(
        (n) => n.id === notificationId || n._id === notificationId
      );
      return {
        notifications: state.notifications.filter(
          (n) => n.id !== notificationId && n._id !== notificationId
        ),
        unreadCount: notification && !notification.isRead
          ? Math.max(0, state.unreadCount - 1)
          : state.unreadCount,
      };
    });
  },

  // Clear all notifications
  clearAll: () => {
    set({ notifications: [], unreadCount: 0 });
  },

  // Set loading state
  setLoading: (isLoading) => {
    set({ isLoading });
  },

  // Update unread count
  setUnreadCount: (count) => {
    set({ unreadCount: count });
  },
}));

export { useNotificationStore };
